/**
 * QS-33 — open-ended (comment / `open_end`) insight redesign.
 *
 * A self-contained card for open-end insights, replacing the flat themed-list +
 * truncated-sample view that the shared `TextResponsesList` renders for Quick Study.
 *
 * Themes view:
 *   - the leading themes as HORIZONTAL bars, sorted highest-first (up to 5), each with
 *     its theme label + share + count and a bar whose length tracks the mention share.
 * Quotes view:
 *   - a quote GALLERY: one featured quote (large portrait + name + blurb + pull-quote)
 *     plus a grid of compact quote cards for every other verbatim, each carrying the
 *     respondent's name, a short demographic blurb and (when we have it) their portrait.
 *
 * Identity is attributed by matching each response back to a fetched twin persona via
 * `persona_id` (the engine echoes it on `responses`), with a conservative attribute
 * fallback. Unmatched quotes still render — anonymously ("Twin") — so coverage never
 * drops. We never invent a name, blurb or portrait we don't have.
 *
 * Theme counts/shares are kept grounded exactly as given (backend clusters + counts).
 * Pure FE / render-only. Net-new component — deck-ui is untouched.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Quote, ChevronRight } from '@digital-twins/design-system/icons'

import { SubsegmentChip } from '@/components/twin-chat'
import { UserAvatar } from '@/components/ui/UserAvatar'
// Deep import (not the study-doc barrel) — read-only reuse of the same slim picker
// fetch the portrait mosaic band uses to fill a missing row image. See the module's
// own doc comment: it only ever hits `getPersonasForPicker` (never `getPersonas`).
import { resolveMosaicPickerImages } from '@/components/study-doc/PortraitMosaicStrip/resolveMosaicPickerImages'
import { cn } from '@/lib/utils'
import { personaThumbUrl } from '@/utils/personaThumbUrl'
import { ComparisonLabelText } from './ComparisonLabelText'
import { MonadicThemeHeatmap } from './MonadicThemeHeatmap'
import { SentimentChip, SentimentGroupChip } from './SentimentChips'
import { bandThemesToHeatmap, groupThemesBySentiment, isOtherTheme, orderThemesOtherLast, themeDisplayName } from './openEndThemeGroups'
import { useThemeOtherLabel } from './useThemeOtherLabel'
import { comboAnswerLabel } from './comboAnswers'
import { resolveQuoteVersionLabel } from './quickStudyPure'
import { useQuickStudyNoun } from './quickStudyNoun'
import { OpenEndQuotesPlot, OpenEndThemesPlot, readQuotePlot, readThemeHeatPlot, readThemePlot, ThemeHeatGridPlot, type ThemeHeatPlot } from './OpenEndPlots'

import {
  findResponseForPersona,
  quoteIdentityBlurb,
  twinDisplayName,
  twinIdentityTagline,
  twinInitials,
  type QuickStudyPersona,
} from './QuickStudyTwinsPanel'

const QS_MAIN = 'qs_main'

// Matches the backend `Theme` (analysis.py: theme/description/count, percent already
// rounded + clamped 0-100) and the page's `ThemeT`. Kept structural so the page can
// pass its existing themes straight through.
export type OpenEndTheme = {
  theme: string
  /** The analysis's rollup flag (plots plan S); absent on stored results. */
  is_other?: boolean
  description?: string
  sentiment?: string
  percent?: number
  count?: number
}

// Structural — same shape the page's `TwinResponse` uses; only `survey_data` is read
// (by `question.name`, e.g. "qs_main") plus `persona_id` / `attributes` for attribution.
// `segment_labels` (QS LP QA #23) echoes Persona.segment_labels for this twin — [] /
// absent for a baseline twin AND for every non-segment-aware run.
export type OpenEndResponse = {
  persona_id?: string
  survey_data?: Record<string, any>
  attributes?: Record<string, any>
  segment_labels?: string[]
  [k: string]: any
}

// Combo quant+OE (Step G) — the Why-tab band-split model. Structural mirror of the result's
// `theme_splits` (Step C/F): one band per drawn score band / weakest dimension. With
// `shared_codeframe` (current production path) every band's `themes` projects ONE shared
// codeframe (labels align across bands → the heatmap view); without it (legacy sessions /
// reconciliation failure) each band carried its OWN clustering — labels don't align, bands
// render one at a time. `definition` is render-ready ("scores 1-2 of 5", "NPS 0-6
// (Detractors)"); a sub-floor band arrives with `n` + an EMPTY `themes` (the under-floor
// state). Band words (Promoters/Detractors/…) live ONLY on this aggregate control.
export type OpenEndThemeSplitBand = {
  id: string
  label?: string
  definition?: string
  n?: number
  themes?: OpenEndTheme[]
}
export type OpenEndThemeSplits = {
  mode: string
  bands?: OpenEndThemeSplitBand[]
}

// Combo Quotes tab — per-quote score badge config. The badge reads the twin's QUANT answer
// (`survey_data[scoreKey]`, a DIFFERENT key from the why) and renders `score/scaleMax` (e.g.
// "2/5", "9/10") — never a bare number, never a band word (numeric-only on individuals).
export type ComboScoreBadge = {
  scoreKey: string
  scaleMax: number
}

// Below this floor the analysis layer ships a band carrying `n` but EMPTY `themes` (Step F
// sub-floor rule); the Why-tab split control renders the honest under-floor state, not blank bars.
const THEME_SPLIT_FLOOR = 8

export type QuickStudyOpenEndCardProps = {
  /** Clustered themes, grounded as given. Sorted by count desc; we re-sort defensively. */
  themes: OpenEndTheme[]
  /** Every twin response — the Quotes view shows ALL verbatims (no cap/sampling). */
  responses: OpenEndResponse[]
  /** The survey question (carries `name`, e.g. "qs_main") for the verbatim resolver. */
  question?: any
  /** The run's sampled personas, used to attribute a name/blurb/portrait to each quote. */
  personas?: QuickStudyPersona[]
  /** Population/audience id the run's twins belong to. When set, a quote still missing
   *  an image after persona/attribute resolution asks the slim picker view (the same
   *  fallback `PortraitMosaicStrip` uses) instead of staying on the initials avatar.
   *  Omitted -> today's behavior (initials fallback only, no fetch). */
  populationId?: string
  /** QS — report context: the report doesn't carry personas / word frequencies, so the
   *  attributed Quotes gallery and the word Cloud can't render meaningfully here. When
   *  set, the card shows ONLY the Themes view and a link that jumps to the source insight
   *  in the main chat thread, where Quotes + Cloud live in full. */
  onJumpToSource?: () => void
  /** Comparison entity labels (e.g. "Landing page A") bolded in theme + quote text. */
  comparisonLabels?: string[]
  /** Report document — show themes + quotes inline with no tabs, clamps, or jump links. */
  documentMode?: boolean
  /** Controlled-tab mode (chat / PlotCard): when set, the card renders ONLY this view and
   *  suppresses the internal Themes|Quotes toggle — the toggle lives upstream in MessageRow's
   *  PlotCard header. When absent, the card manages its own tab state (byte-identical legacy). */
  activeTab?: OpenEndTab
  /** Paired with `activeTab`; accepted for symmetry. Unused internally in controlled mode —
   *  the lifted header toggle upstream owns the change handler. */
  onTabChange?: (t: OpenEndTab) => void
  /** T3 — the quotes `responses` were sliced to empty by a segment cut (chat/controlled path).
   *  Threads to QuotesView so its empty state reads "No verbatims match the selected segments."
   *  rather than the false "None captured". Ignored in report/document mode. */
  filteredEmpty?: boolean
  /** Combo Why-tab (Step G) — when present the Themes view renders a band-split control ("All" +
   *  one option per drawn band) above the theme bars. Absent → the view is byte-identical to the
   *  plain-OE themes view (non-combo cards are unaffected). */
  themeSplits?: OpenEndThemeSplits | null
  /** Combo (Step G) — the `survey_data` key carrying each twin's why-verbatim (e.g. "qs_main_why"),
   *  DISTINCT from the quant answer key. When set the Quotes view reads the why from this key
   *  instead of resolving the question name (which would return the score). */
  whyKey?: string
  /** Combo Quotes tab (Step G) — renders a `score/scaleMax` badge on every quote from the twin's
   *  own quant answer. Absent → no badge (plain OE unchanged). */
  scoreBadge?: ComboScoreBadge
  /** W0.7 — monadic quotes: item_id → version label; each pooled quote then carries a chip
   *  naming which creative/concept/claim the twin saw. Absent → no version chips (unchanged). */
  versionLabelById?: Record<string, string>
  /** Combo Drivers only — chart-type for the themes view when `themeSplits` carries a shared
   *  codeframe: 'heatmap' (default) renders the themes × bands grid; 'bars' keeps the band
   *  selector + per-band bars. Ignored when the adapter yields null (legacy per-band data,
   *  <2 themed bands, or no splits). */
  themesView?: 'heatmap' | 'bars'
  /** Wave 7 (Q12): Python's stamped `theme_plot` (choice-bars). When present (and no combo band
   *  split rides along) the Themes view draws it through the shared plot; stored results carry
   *  none and keep `ThemesView` as their legacy reader. */
  themePlot?: unknown
  /** Wave 7 (Q16): Python's stamped `quote_plot` (quote-list gallery). When present the Quotes
   *  view draws it (speaker, chips and featured as stamped); stored results keep the browser
   *  gallery as their legacy reader. */
  quotePlot?: unknown
  /** W7-T4b (Q14): Python's stamped `theme_splits_plot` (heat-grid, themes x score bands). The combo
   *  heatmap view draws it; stored results keep `bandThemesToHeatmap` + `MonadicThemeHeatmap`. */
  themeSplitsPlot?: unknown
  className?: string
}

// Show the leading themes (sorted highest-first). Horizontal bars read more easily than
// a podium and let us surface more than three.
const MAX_THEMES = 5

// ── pure model ──────────────────────────────────────────────────────────────

export interface QuoteItem {
  key: string
  text: string
  /** Display name — the twin's name, or "Twin" when we couldn't attribute it. */
  name: string
  /** Short identity blurb — B2B: "Title · Company descriptor"; else demographic
   *  (age · gender · country); "" when unknown. See `twinIdentityTagline`. */
  blurb: string
  initials: string
  imageUrl?: string
  /** persona_id backing this quote (matched persona's id, or the response's own
   *  persona_id when unmatched). Undefined for a response that carried no persona_id
   *  at all. Used ONLY to key the picker-fallback fetch (see `populationId` on
   *  `QuickStudyOpenEndCardProps`) — never rendered. */
  personaId?: string
  /** True when the quote was attributed to a specific twin persona. */
  matched: boolean
  /** QS LP QA #7/#23 — the twin's PRIMARY authored subsegment label (the first of
   *  `response.segment_labels`), rendered as a chip after the quote text. "" when the
   *  run wasn't segment-aware or this twin carries no label. */
  segmentLabel: string
  /** Combo (Step G) — this twin's own quant answer as `score/scaleMax` (e.g. "2/5"), shown as a
   *  neutral numeric badge on the quote. Absent for a plain-OE quote / a twin with no finite score. */
  scoreBadge?: string
  /** W0.7 — the monadic VERSION this twin saw (per_item label resolved from the response's
   *  `item_id`), rendered as a chip so a pooled quote list always says which
   *  creative/concept/claim each verbatim reacts to. Absent when no version map is passed. */
  versionLabel?: string
  /** Combo — what this twin ANSWERED on the closed half, as its human LABEL ("B — Depth", "9/10"),
   *  so the why can be read against the choice it explains. Never the stored option code. Absent
   *  off the combo path and whenever the answer resolves to nothing readable. */
  answerLabel?: string
}

// The chip row under a quote: what the twin answered, which version they saw, then their segment.
// Extracted so the featured and grid cards cannot drift. A quote carrying ONLY a segment chip keeps
// its original bare-chip render, so every pre-combo quote is byte-identical.
function QuoteChipRow({ item }: { item: QuoteItem }) {
  // Bug 12/13: the score is already shown as the top-right QuoteScoreBadge, so drop a version or
  // segment chip that merely repeats it — the rating must never render twice.
  const score = (item.scoreBadge ?? '').trim()
  const versionLabel = item.versionLabel && item.versionLabel.trim() !== score ? item.versionLabel : ''
  const segmentLabel = item.segmentLabel && item.segmentLabel.trim() !== score ? item.segmentLabel : ''
  if (item.answerLabel || versionLabel) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {item.answerLabel && <SubsegmentChip label={item.answerLabel} testId="qs-quote-answer-chip" />}
        {versionLabel && <SubsegmentChip label={versionLabel} testId="qs-quote-version-chip" />}
        {segmentLabel && <SubsegmentChip label={segmentLabel} testId="qs-quote-segment-chip" />}
      </div>
    )
  }
  return segmentLabel ? (
    <SubsegmentChip label={segmentLabel} className="mt-2" testId="qs-quote-segment-chip" />
  ) : null
}

// The first authored subsegment label on a response ("" when absent/not segment-aware).
function primarySegmentLabel(response: OpenEndResponse): string {
  const labels = response.segment_labels
  if (!Array.isArray(labels) || labels.length === 0) return ''
  return String(labels[0] ?? '').trim()
}

function resolveQuestionName(question: any, response: OpenEndResponse): string {
  const sd = response.survey_data ?? {}
  if (question?.name && sd[question.name] !== undefined) return question.name
  return QS_MAIN
}

// Pull the verbatim out of one response. Open-end answers are strings; we still flatten
// the rare array shape so nothing is dropped, and skip empties.
export function extractQuoteText(response: OpenEndResponse, question: any): string {
  const name = resolveQuestionName(question, response)
  const v = response.survey_data?.[name]
  if (v == null) return ''
  if (Array.isArray(v)) return v.map((x) => String(x)).join(' ').trim()
  if (typeof v === 'object') return ''
  return String(v).trim()
}

/**
 * Attribute every text response to a twin persona. Primary path: `persona_id` (echoed
 * by the engine). Fallback: the panel's conservative attribute-overlap matcher for
 * legacy saved sessions that stripped the id. Returns a map of response index → persona.
 */
function matchPersonas(
  responses: OpenEndResponse[],
  personas: QuickStudyPersona[],
): Map<number, QuickStudyPersona> {
  const out = new Map<number, QuickStudyPersona>()
  if (!personas.length) return out

  const byId = new Map(personas.map((p) => [p.personaId, p]))
  responses.forEach((r, i) => {
    if (r.persona_id && byId.has(r.persona_id)) out.set(i, byId.get(r.persona_id)!)
  })

  // Attribute fallback only for responses we couldn't match by id — and only for
  // personas not already claimed, so we never label two quotes with the same twin.
  const claimedResp = new Set(out.keys())
  const claimedPersona = new Set(out.values())
  for (const persona of personas) {
    if (claimedPersona.has(persona)) continue
    const resp = findResponseForPersona(persona, responses as any)
    if (!resp) continue
    const idx = responses.indexOf(resp as OpenEndResponse)
    if (idx >= 0 && !claimedResp.has(idx)) {
      out.set(idx, persona)
      claimedResp.add(idx)
      claimedPersona.add(persona)
    }
  }
  return out
}

// A quote's name straight from the response's own attributes — used when no persona
// object matched. persona_id IS present on /run response rows; matchPersonas() above
// prefers it and this fallback only fires for the rare miss (legacy saved sessions that
// stripped the id, or a persona no longer in the loaded pool). We NEVER invent data: we
// only read a real name the response already carries. The quote SUBTITLE for this same
// fallback comes from the shared `quoteIdentityBlurb` (Bug 12) so a matched and an
// unmatched quote read the same way — one format, exact age dropped, B2B rule applied.
function nameFromAttributes(attributes: Record<string, any> | undefined): string {
  const attrs = attributes ?? {}
  const raw = attrs.display_name ?? attrs.name ?? attrs.persona_name ?? ''
  return String(raw).trim()
}

// The same attribute keys the portrait mosaic band reads (buildPortraitMosaicTiles):
// response rows carry the portrait even when no persona record was matched, so the
// unmatched quote-card path must not silently drop to initials (live QA 2026-09-03).
function imageFromAttributes(attributes: Record<string, any> | undefined): string {
  const attrs = attributes ?? {}
  const raw = attrs.image_url ?? attrs.imageUrl ?? ''
  return String(raw).trim()
}

function initialsFromName(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || 'T'
  )
}

// Combo (Step G): the why-verbatim lives on a DISTINCT survey_data key (`whyKey`, e.g.
// "qs_main_why") from the quant answer, so a combo quote reads that key directly rather than the
// resolved question name (which returns the score). Same flatten/skip-empty rules as extractQuoteText.
function extractWhyText(response: OpenEndResponse, whyKey: string): string {
  const v = response.survey_data?.[whyKey]
  if (v == null) return ''
  if (Array.isArray(v)) return v.map((x) => String(x)).join(' ').trim()
  if (typeof v === 'object') return ''
  return String(v).trim()
}

// Combo (Step G): the `score/scaleMax` badge string for one response (e.g. "2/5", "9/10"), read
// from the twin's own quant answer. "" when the twin carries no finite score. Numeric only — a
// band word is NEVER attached to an individual (blame-optics rule, review #6).
export function comboScoreBadgeText(response: OpenEndResponse, cfg: ComboScoreBadge): string {
  let raw = response.survey_data?.[cfg.scoreKey]
  // Monadic combo: the score is stored keyed by the version the twin saw ({item_id: score}) —
  // unwrap that twin's own entry (same rule as `comboAnswerLabel`, so chip and badge agree).
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    const rec = raw as Record<string, unknown>
    const keyed = response.item_id ? rec[String(response.item_id)] : undefined
    const entries = Object.values(rec)
    raw = keyed !== undefined ? keyed : entries.length === 1 ? entries[0] : undefined
  }
  const score = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(score)) return ''
  return `${score}/${cfg.scaleMax}`
}

export function buildQuoteItems(
  responses: OpenEndResponse[],
  question: any,
  personas: QuickStudyPersona[] = [],
  // Combo mode (Step G): `whyKey` reads the verbatim from a distinct key; `scoreBadge` stamps each
  // quote with its twin's own `score/scaleMax`. `versionLabelById` (W0.7, monadic quotes)
  // resolves each response's `item_id` to its version label so the quote carries a version chip.
  // All absent → plain-OE behavior, byte-identical.
  opts: { whyKey?: string; scoreBadge?: ComboScoreBadge; versionLabelById?: Record<string, string> } = {},
): QuoteItem[] {
  const matched = matchPersonas(responses, personas)
  const items: QuoteItem[] = []
  responses.forEach((r, i) => {
    const text = opts.whyKey ? extractWhyText(r, opts.whyKey) : extractQuoteText(r, question)
    if (!text) return
    const scoreBadge = opts.scoreBadge ? comboScoreBadgeText(r, opts.scoreBadge) || undefined : undefined
    const versionLabel = resolveQuoteVersionLabel({
      versionLabelById: opts.versionLabelById,
      itemId: r.item_id,
      surveyData: r.survey_data ?? {},
      quoteText: text,
    })
    // Combo only (`whyKey` is what marks the quote as a why): the twin's own answer to the closed
    // half, as its human label. Shares the ledger's derivation so the chat chip and the report
    // chip can never say different things about the same twin.
    const answerLabel = opts.whyKey
      ? comboAnswerLabel(r.survey_data ?? {}, question, r.item_id ? String(r.item_id) : undefined) || undefined
      : undefined
    const persona = matched.get(i)
    const segmentLabel = primarySegmentLabel(r)
    if (persona) {
      items.push({
        key: persona.personaId || `q-${i}`,
        text,
        name: twinDisplayName(persona),
        blurb: twinIdentityTagline(persona),
        initials: twinInitials(persona),
        imageUrl: persona.imageUrl || undefined,
        personaId: persona.personaId || undefined,
        matched: true,
        segmentLabel,
        scoreBadge,
        versionLabel,
        answerLabel,
      })
    } else {
      // No matched persona: fall back to the name + subtitle the response's attributes carry.
      // Bug 12 — the SAME `quoteIdentityBlurb` the matched path uses (via twinIdentityTagline),
      // so matched and unmatched quote cards render one consistent subtitle format.
      const attrName = nameFromAttributes(r.attributes)
      const blurb = quoteIdentityBlurb(r.attributes)
      items.push({
        key: r.persona_id || `q-${i}`,
        text,
        name: attrName || 'Twin',
        blurb,
        initials: attrName ? initialsFromName(attrName) : 'T',
        imageUrl: imageFromAttributes(r.attributes) || undefined,
        personaId: r.persona_id || undefined,
        matched: Boolean(attrName),
        segmentLabel,
        scoreBadge,
        versionLabel,
        answerLabel,
      })
    }
  })
  return items
}

// Featured quote: a portrait makes the hero land, so prefer the first quote we have an
// image for; else the first attributed quote; else the first quote. Deterministic — no
// invented "best quote" ranking.
export function pickFeaturedIndex(items: QuoteItem[]): number {
  if (items.length === 0) return -1
  const withImage = items.findIndex((i) => i.imageUrl)
  if (withImage >= 0) return withImage
  const attributed = items.findIndex((i) => i.matched)
  if (attributed >= 0) return attributed
  return 0
}

// Bar fill fraction (0..1) per theme. Mention shares are absolute (a 69% theme fills 69%
// of the track); count-only themes normalize to the largest count so the leader fills the
// track. Equal values → equal length. Pure + exported for unit coverage.
export function themeBarFractions(themes: OpenEndTheme[]): number[] {
  const usePercent = themes.length > 0 && themes.every((t) => typeof t.percent === 'number')
  if (usePercent) {
    return themes.map((t) => Math.max(0, Math.min(1, (t.percent ?? 0) / 100)))
  }
  const maxCount = Math.max(1, ...themes.map((t) => t.count ?? 0))
  return themes.map((t) => Math.max(0, Math.min(1, (t.count ?? 0) / maxCount)))
}

/** Track behind theme bar fills — neutral grayscale, low-contrast on the chat surface. */
export const THEME_BAR_TRACK_CLASS = 'bg-neutral-200/45'

/** Canvas chart track — the shared `--chart-bar-track` token (theme-aware, matches the
 *  DOM TrackBar primitive). Resolved for canvas via `resolveCanvasColor`. */
export const CHART_BAR_TRACK_COLOR = 'var(--chart-bar-track)'

/** Shared theme bar thickness (grouped monadic + open-end theme rows). */
export const THEME_BAR_HEIGHT_CLASS = 'h-2'

// Sentiment chips now live in the leaf `SentimentChips.tsx` (so `MonadicThemeHeatmap` can
// consume them without closing a module cycle with this card — same precedent as the
// "Other" helpers below); re-exported here (imported above) so existing importers keep their path.
export { SentimentChip, SentimentGroupChip }

// "Other" roll-up helpers now live in the leaf util `openEndThemeGroups` (to break the
// theme-grouping import cycle); re-exported here (imported above) so existing importers keep their path.
export { isOtherTheme, orderThemesOtherLast }

function shareLabel(t: OpenEndTheme): string {
  const pct = typeof t.percent === 'number' ? `${Math.round(t.percent)}%` : null
  const n = typeof t.count === 'number' ? `${t.count}` : null
  if (pct && n) return `${pct} · ${n}`
  return pct || (n ? `${n} mention${t.count === 1 ? '' : 's'}` : '')
}

// ── themes view ─────────────────────────────────────────────────────────────

function ThemesView({
  themes,
  reportMode = false,
  documentMode = false,
  comparisonLabels = [],
  themeSplits,
  themesView = 'heatmap',
  heatPlot = null,
}: {
  themes: OpenEndTheme[]
  reportMode?: boolean
  documentMode?: boolean
  comparisonLabels?: string[]
  // Combo Why-tab (Step G): a band-split control cutting the pooled why-themes by each twin's own
  // drawn band / weakest dimension. Absent → the control never renders and this view is
  // byte-identical to the plain-OE themes view (non-combo OE cards are unaffected).
  themeSplits?: OpenEndThemeSplits | null
  // Combo Drivers only — see QuickStudyOpenEndCardProps.themesView. Ignored (bandHeat null)
  // outside the combo shared-codeframe path.
  themesView?: 'heatmap' | 'bars'
  // W7-T4b: Python's band heat grid (`theme_splits_plot`); when present the heatmap view draws it.
  heatPlot?: ThemeHeatPlot | null
}) {
  const { noun: respondents } = useQuickStudyNoun()
  // Combo band heatmap: null unless the backend stamped a shared codeframe AND ≥2 bands carry
  // themes (see bandThemesToHeatmap). When present and the caller wants the heatmap view, render
  // the themes × bands grid INSTEAD of the band selector + per-band bars below — no band selector
  // in the heatmap view. Falls through to the existing selector+bars view, byte-identical to
  // before, when the adapter yields null (legacy per-band data, <2 themed bands, no splits) or
  // `themesView === 'bars'`. Computed up front (with every hook below) so the early return further
  // down never changes the hook call order between renders.
  const bandHeat = useMemo(() => bandThemesToHeatmap(themeSplits ?? null), [themeSplits])
  const otherLabel = useThemeOtherLabel()

  const bands = useMemo(() => themeSplits?.bands ?? [], [themeSplits])
  // No split control in the flat report/document projection (it has no interactive chrome).
  const hasSplit = bands.length > 0 && !documentMode
  // 'all' = the pooled `themes`; a band id = that band's own clustering. Band words
  // (Promoters/Detractors/…) appear ONLY on this aggregate control — never on an individual.
  const [selectedBandId, setSelectedBandId] = useState<string>('all')
  const selectedBand = selectedBandId === 'all' ? null : bands.find((b) => b.id === selectedBandId) ?? null
  const activeThemes = selectedBand ? selectedBand.themes ?? [] : themes
  // Sub-floor band (Step F): a band with n<floor arrives with `n` + EMPTY `themes`, so the "too few
  // twins" state fires only when the band is BOTH themeless AND under the floor. (Zero-member bands
  // are already omitted server-side; a themeless band at/above the floor — no clusters formed —
  // falls through to the generic "no themes clustered" empty state below instead.)
  const bandThemeless =
    Boolean(selectedBand) && !(selectedBand!.themes ?? []).some((t) => (t.theme || '').trim().length > 0)
  const bandUnderFloor = bandThemeless && (selectedBand!.n ?? 0) < THEME_SPLIT_FLOOR

  // Sort by count (then share) desc so the bars always read highest-first, independent of
  // upstream ordering. Drop empty labels (grounded data only). The suppressed-theme
  // "Other" roll-up always sinks to the end regardless of its pooled count.
  const ranked = useMemo(
    () =>
      orderThemesOtherLast(
        [...activeThemes]
          .filter((t) => (t.theme || '').trim().length > 0)
          .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || (b.percent ?? 0) - (a.percent ?? 0)),
      ),
    [activeThemes],
  )

  // Chat combo Drivers only by intent: no report/document surface threads `themeSplits` today,
  // so this branch is unreachable there — if one ever does, decide the document treatment
  // deliberately (the bars path below suppresses split chrome in documentMode via `hasSplit`).
  if (heatPlot && themesView === 'heatmap') {
    return (
      <div data-testid="qs-combo-band-heatmap">
        <ThemeHeatGridPlot plot={heatPlot} documentMode={documentMode} testIdPrefix="qs-combo-band-heat-grid" />
      </div>
    )
  }
  if (bandHeat && themesView === 'heatmap') {
    return (
      <div className="px-1 pt-1" data-testid="qs-combo-band-heatmap">
        <MonadicThemeHeatmap themes={bandHeat.themes} columns={bandHeat.columns} comparisonLabels={comparisonLabels} />
        {bandHeat.excluded.length > 0 && (
          <p className="mt-2 text-xs text-twin-chat-fg-muted" data-testid="qs-combo-band-heatmap-underfloor">
            Too few {respondents} to theme: {bandHeat.excluded.map((b) => `${b.label} (n=${b.n})`).join(', ')}. Read their
            quotes directly.
          </p>
        )}
      </div>
    )
  }

  // The band-split control: "All" (pooled) + one option per band, each labelled with its
  // definition + n. Rendered ABOVE the bars; null (and DOM-absent) when there is no split, so a
  // plain-OE card renders exactly the bare <ul>/<p> it did before.
  const splitControl = hasSplit ? (
    <div className="flex flex-wrap items-center gap-1.5" role="group" data-testid="qs-combo-split-control">
      {[{ id: 'all', label: 'All', definition: '', n: undefined as number | undefined }, ...bands].map((opt) => {
        const isBand = opt.id !== 'all'
        const active = selectedBandId === opt.id
        const optLabel = isBand
          ? [
              opt.label ?? opt.id,
              opt.definition ? `(${opt.definition})` : '',
              typeof opt.n === 'number' ? `· n=${opt.n}` : '',
            ]
              .filter(Boolean)
              .join(' ')
          : 'All'
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={active}
            onClick={() => setSelectedBandId(opt.id)}
            data-testid={`qs-combo-split-option-${opt.id}`}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
              active
                ? 'border-twin-chat-send bg-twin-chat-send/10 text-twin-chat-fg'
                : 'border-twin-chat-divider text-twin-chat-fg-muted hover:text-twin-chat-fg',
            )}
          >
            {optLabel}
          </button>
        )
      })}
    </div>
  ) : null

  // Under-floor band: reuse the existing muted centered empty-state pattern, keeping the control
  // visible so the user can switch back to "All".
  if (bandUnderFloor) {
    return (
      <>
        {splitControl}
        <p
          className="px-1 py-6 text-center text-sm text-twin-chat-fg-muted"
          data-testid="qs-combo-split-underfloor"
        >
          {respondents === 'respondents'
            ? `Too few respondents in this band to theme (n=${selectedBand!.n ?? 0}). Read their quotes directly.`
            : `Too few twins in this band to theme (n=${selectedBand!.n ?? 0}) — read their quotes directly.`}
        </p>
      </>
    )
  }

  if (ranked.length === 0) {
    // Report/document: omit the figure entirely — never point readers at tabs or chat.
    if (documentMode || reportMode) {
      return null
    }
    return (
      <>
        {splitControl}
        <p className="px-1 py-6 text-center text-sm text-twin-chat-fg-muted">
          No themes were clustered for this question — check the Quotes view for the verbatims.
        </p>
      </>
    )
  }

  const top = documentMode ? ranked : ranked.slice(0, MAX_THEMES)
  // Group the leading themes into sentiment sections (Positive / Mixed / Negative, "Other"
  // last) — mirrors the monadic heatmap's grouping so a study's Themes read the same way in
  // both places. `flatThemes` is the resulting render order; bar widths are computed against it
  // (via `indexOf`) so they stay correct once themes are re-shuffled into sections. The strongest
  // theme keeps its emphasis by identity: `topTheme = top[0]` (ranked is count-desc, so top[0] is
  // the strongest non-Other theme), matched by reference in `renderThemeRow`.
  const { groups, other } = groupThemesBySentiment(top)
  const flatThemes = [...groups.flatMap((g) => g.themes), ...other]
  const fractions = themeBarFractions(flatThemes)
  const topTheme = top[0]
  const indexOf = new Map(flatThemes.map((t, i) => [t, i]))

  function renderThemeRow(t: OpenEndTheme, key: string, isOtherRow: boolean) {
    const share = shareLabel(t)
    const fi = indexOf.get(t) ?? 0
    // Floor the rendered width so a tiny-but-present theme still shows a sliver.
    const widthPct = Math.max(3, Math.round(fractions[fi] * 100))
    const isTop = t === topTheme && !isOtherRow
    return (
      <li key={key} data-testid="qs-open-end-theme-row" data-theme-other={isOtherRow || undefined}>
        <div className="flex items-baseline justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span
              className={cn('min-w-0 truncate text-sm font-medium', isOtherRow ? 'text-twin-chat-fg-muted' : 'text-twin-chat-fg')}
              title={themeDisplayName(t, otherLabel)}
            >
              <ComparisonLabelText text={themeDisplayName(t, otherLabel)} labels={comparisonLabels} />
            </span>
          </span>
          {share && (
            <span className="shrink-0 text-xs tabular-nums text-twin-chat-fg-muted">{share}</span>
          )}
        </div>
        <div className={cn('mt-1 w-full overflow-hidden rounded-full', THEME_BAR_HEIGHT_CLASS, THEME_BAR_TRACK_CLASS)}>
          <div
            className={cn('h-full rounded-full', isTop ? 'bg-primary' : 'bg-primary/55', isOtherRow && 'opacity-60')}
            style={{ width: `${widthPct}%` }}
          />
        </div>
      </li>
    )
  }

  return (
    <>
      {splitControl}
      <ul className="space-y-3 px-1 pt-1" data-testid="qs-open-end-themes">
        {groups.map((g) => (
          <Fragment key={g.group}>
            <li data-testid="qs-open-end-theme-group" data-group={g.group} className="pt-1 first:pt-0">
              <SentimentGroupChip group={g.group} />
            </li>
            {g.themes.map((t) => renderThemeRow(t, `${g.group}-${t.theme}`, false))}
          </Fragment>
        ))}
        {other.map((t) => renderThemeRow(t, `other-${t.theme}`, true))}
      </ul>
    </>
  )
}

// ── quotes view ─────────────────────────────────────────────────────────────

function QuoteAvatar({
  item,
  size,
}: {
  item: QuoteItem
  size: 'sm' | 'xl'
}) {
  const [broken, setBroken] = useState(false)
  // One-shot fallback (finding 7, image compression): try the pre-generated
  // 128px thumbnail first; if it 404s (not backfilled for this persona yet),
  // retry the original url once before falling back to the initials avatar.
  const [thumbFailed, setThumbFailed] = useState(false)
  const dims = size === 'xl' ? 'h-20 w-20' : 'h-9 w-9'
  const ring = size === 'xl' ? 'ring-2 ring-twin-chat-send/40 ring-offset-2 ring-offset-twin-chat-surface' : ''
  const thumbUrl = item.imageUrl ? personaThumbUrl(item.imageUrl, 128) : undefined
  const displayImageUrl = thumbFailed || !thumbUrl ? item.imageUrl : thumbUrl
  if (displayImageUrl && !broken) {
    return (
      <img
        src={displayImageUrl}
        alt={item.name}
        onError={() => {
          if (!thumbFailed && thumbUrl && thumbUrl !== item.imageUrl) {
            setThumbFailed(true)
            return
          }
          setBroken(true)
        }}
        className={cn(dims, ring, 'shrink-0 rounded-full border border-twin-chat-divider object-cover')}
      />
    )
  }
  return <UserAvatar initials={item.initials} name={item.name} size={size} className={ring} />
}

// QS LP QA #7 — subsegment chip: rendered AFTER the quote text on every persona quote
// card (the hero card's `chip` slot already carries the STIMULUS identity in the header;
// this is a DIFFERENT chip — the twin's authored subsegment — placed below the verbatim).
// Component itself now lives in `@/components/twin-chat/SubsegmentChip` so the Audience
// tab twin list/preview card can render the exact same chip.

// Combo (Step G) — the neutral numeric score badge shown on a quote (`score/scaleMax`). Numeric
// ONLY, neutral styling (no red/band coloring on a person), per the blame-optics rule (review #6).
function QuoteScoreBadge({ text }: { text: string }) {
  return (
    <span
      className="shrink-0 rounded-full bg-twin-chat-bubble/60 px-2 py-0.5 text-xs font-medium tabular-nums text-twin-chat-fg"
      data-testid="qs-quote-score-badge"
    >
      {text}
    </span>
  )
}

// The trailing chips under a quote (W0.7 version chip + LP QA #7/#23 subsegment chip). Shared
// by the featured and grid cards so the two can't drift.
// Bug 13(a) — a chip whose label is literally the score string (e.g. a band named "4/5") is
// DROPPED: the QuoteScoreBadge already prints that value in the header, so rendering it again
// as a chip would show the score twice. Returns null when nothing groundable remains.
export function FeaturedQuote({
  item,
  chip,
  comparisonLabels = [],
}: {
  item: QuoteItem
  chip?: React.ReactNode
  comparisonLabels?: string[]
}) {
  return (
    <figure
      className="relative overflow-hidden rounded-2xl border border-twin-chat-send/30 bg-transparent p-6"
      data-testid="qs-quote-featured"
    >
      <div className="flex items-center gap-4">
        <QuoteAvatar item={item} size="xl" />
        <figcaption className="min-w-0 flex-1">
          <p className="truncate text-lg font-semibold text-twin-chat-fg">{item.name}</p>
          {item.blurb && <p className="truncate text-sm text-twin-chat-fg-muted">{item.blurb}</p>}
        </figcaption>
        {item.scoreBadge && <QuoteScoreBadge text={item.scoreBadge} />}
        {chip}
      </div>
      <blockquote className="mt-5 text-xl font-medium leading-relaxed text-twin-chat-fg">
        “<ComparisonLabelText text={item.text} labels={comparisonLabels} />”
      </blockquote>
      {/* Answer chip (what they picked) + version chip (which stimulus they saw) + segment. */}
      <QuoteChipRow item={item} />
    </figure>
  )
}

// A verbatim longer than this (~6 lines at text-sm in a ~250px card) is clamped
// behind a "Read more" toggle. A character heuristic keeps the toggle deterministic
// (and unit-testable) without measuring layout — it only needs to match the clamp
// closely enough that short quotes never show a pointless toggle.
const QUOTE_CLAMP_CHARS = 220

// `chip` is an optional trailing slot in the header (used by the monadic view to tag a
// quote with its variant); omitted everywhere else so the existing card is unchanged.
export function QuoteCard({
  item,
  chip,
  comparisonLabels = [],
  documentMode = false,
}: {
  item: QuoteItem
  chip?: React.ReactNode
  comparisonLabels?: string[]
  documentMode?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const isLong = !documentMode && item.text.length > QUOTE_CLAMP_CHARS
  return (
    <figure
      className="flex h-full flex-col gap-3 rounded-xl border border-twin-chat-divider bg-transparent p-4 transition-colors hover:border-twin-chat-send/40"
      data-testid="qs-quote-card"
    >
      <div className="flex items-center gap-2.5">
        <QuoteAvatar item={item} size="sm" />
        <figcaption className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-twin-chat-fg">{item.name}</p>
          {item.blurb && <p className="truncate text-xs text-twin-chat-fg-muted">{item.blurb}</p>}
        </figcaption>
        {item.scoreBadge && <QuoteScoreBadge text={item.scoreBadge} />}
        {chip}
      </div>
      <blockquote
        className={cn(
          'text-sm leading-relaxed text-twin-chat-fg',
          isLong && !expanded && 'line-clamp-6',
        )}
      >
        “<ComparisonLabelText text={item.text} labels={comparisonLabels} />”
      </blockquote>
      {/* Answer chip (what they picked) + version chip (which stimulus they saw) + segment. */}
      <QuoteChipRow item={item} />
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-auto self-start text-xs font-medium text-foreground hover:underline"
          data-testid="qs-quote-readmore"
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </figure>
  )
}

/** Grid of non-featured quotes — each card carries portrait, name, blurb and verbatim. */
function QuotesGrid({
  items,
  total,
  comparisonLabels = [],
  documentMode = false,
}: {
  items: QuoteItem[]
  total: number
  comparisonLabels?: string[]
  documentMode?: boolean
}) {
  return (
    <div className="space-y-2" data-testid="qs-open-end-quote-grid">
      <span className="block text-xs tabular-nums text-twin-chat-fg-muted" data-testid="qs-quotes-count">
        {total} quotes
      </span>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((item) => (
          <QuoteCard key={item.key} item={item} comparisonLabels={comparisonLabels} documentMode={documentMode} />
        ))}
      </div>
    </div>
  )
}

function QuotesView({
  responses,
  question,
  personas,
  populationId,
  comparisonLabels = [],
  documentMode = false,
  filteredEmpty = false,
  whyKey,
  scoreBadge,
  versionLabelById,
}: {
  responses: OpenEndResponse[]
  question?: any
  personas?: QuickStudyPersona[]
  /** See `QuickStudyOpenEndCardProps.populationId`. */
  populationId?: string
  comparisonLabels?: string[]
  documentMode?: boolean
  /** T3 — the responses were sliced to empty by a selected segment cut (not simply never
   *  captured). Switches the empty copy so it doesn't falsely claim nothing was captured. */
  filteredEmpty?: boolean
  /** Combo (Step G) — read the verbatim from this key and stamp a `score/scaleMax` badge. */
  whyKey?: string
  scoreBadge?: ComboScoreBadge
  /** W0.7 — monadic quotes: item_id → version label, so each pooled quote carries a chip
   *  naming which creative/concept/claim the twin saw. Absent → no version chips (unchanged). */
  versionLabelById?: Record<string, string>
}) {
  const rawItems = useMemo(
    () => buildQuoteItems(responses, question, personas ?? [], { whyKey, scoreBadge, versionLabelById }),
    [responses, question, personas, whyKey, scoreBadge, versionLabelById],
  )

  // Portraits still missing after persona/attribute resolution (live QA 2026-09-02: the
  // Quotes view rendered initials-only for respondents the mosaic band above showed photos
  // for, because no persona records reach here and the response rows carried no
  // attributes.image_url). Keyed on a stable, sorted, joined string of the missing
  // personaIds — mirrors PortraitMosaicStrip's own `missingPickerKey` gating — so a
  // re-render whose missing set is unchanged (e.g. a version-filter click) never refires
  // an identical request.
  const missingImageItems = useMemo(
    () => rawItems.filter((item) => !item.imageUrl && item.personaId),
    [rawItems],
  )
  const missingImageKey = useMemo(
    () =>
      missingImageItems
        .map((item) => item.personaId)
        .sort()
        .join(','),
    [missingImageItems],
  )

  const [fetchedImages, setFetchedImages] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!populationId || missingImageItems.length === 0) {
      setFetchedImages({})
      return
    }

    let cancelled = false

    void resolveMosaicPickerImages(
      populationId,
      missingImageItems.map((item) => ({ personaId: item.personaId as string, name: item.name })),
    ).then((next) => {
      if (!cancelled) {
        setFetchedImages(next)
      }
    })

    return () => {
      cancelled = true
    }
    // Keyed on `missingImageKey` rather than `rawItems`'s object identity — `rawItems` gets
    // a new array identity on every parent re-render even when the set of quotes missing an
    // image hasn't changed, which would refire an identical `getPersonasForPicker` request
    // every time (same regression PortraitMosaicStrip's own fetch effect guards against).
    // `missingImageItems` is read from the closure intentionally (not listed as a
    // dependency): by the time this effect body runs, its content is exactly what
    // `missingImageKey` already gates on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationId, missingImageKey])

  // Fetched url used ONLY when the item has no imageUrl of its own — a real
  // persona/attribute image always wins.
  const items = useMemo(() => {
    if (Object.keys(fetchedImages).length === 0) {
      return rawItems
    }
    return rawItems.map((item) => {
      if (item.imageUrl || !item.personaId) {
        return item
      }
      const url = fetchedImages[item.personaId]
      return url ? { ...item, imageUrl: url } : item
    })
  }, [rawItems, fetchedImages])

  // Per-version filter (Kevin's QA ask): on a monadic card the pooled gallery mixes every
  // ad/asset's quotes — a chip row filters to one version at a time. Derived off the items'
  // own version chips (first-seen order), so it exists exactly when versions exist and can
  // never offer an empty cut. Hidden in documentMode (the report document is a static read).
  const [versionFilter, setVersionFilter] = useState<string | null>(null)
  const versionOptions = useMemo(() => {
    const seen: Array<string> = []
    for (const it of items) {
      const v = (it.versionLabel || '').trim()
      if (v && !seen.includes(v)) seen.push(v)
    }
    return seen
  }, [items])
  const activeFilter = versionFilter && versionOptions.includes(versionFilter) ? versionFilter : null
  const shown = useMemo(
    () => (activeFilter ? items.filter((it) => (it.versionLabel || '').trim() === activeFilter) : items),
    [items, activeFilter],
  )
  const featuredIndex = useMemo(() => pickFeaturedIndex(shown), [shown])

  if (items.length === 0) {
    return (
      <p className="px-1 py-6 text-center text-sm text-twin-chat-fg-muted" data-testid="qs-open-end-quotes">
        {filteredEmpty
          ? 'No verbatims match the selected segments.'
          : 'No verbatims were captured for this question.'}
      </p>
    )
  }

  const featured = shown[featuredIndex]
  const rest = shown.filter((_, i) => i !== featuredIndex)

  return (
    <div className="space-y-4" data-testid="qs-open-end-quotes">
      {!documentMode && versionOptions.length >= 2 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="qs-quote-version-filter">
          <button
            type="button"
            onClick={() => setVersionFilter(null)}
            aria-pressed={activeFilter === null}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs transition-colors',
              activeFilter === null
                ? 'border-transparent bg-foreground text-background'
                : 'border-twin-chat-divider text-twin-chat-fg-muted hover:text-twin-chat-fg',
            )}
            data-testid="qs-quote-version-filter-all"
          >
            All · {items.length}
          </button>
          {versionOptions.map((v) => {
            const count = items.filter((it) => (it.versionLabel || '').trim() === v).length
            return (
              <button
                key={v}
                type="button"
                onClick={() => setVersionFilter(activeFilter === v ? null : v)}
                aria-pressed={activeFilter === v}
                className={cn(
                  'max-w-56 truncate rounded-full border px-2.5 py-1 text-xs transition-colors',
                  activeFilter === v
                    ? 'border-transparent bg-foreground text-background'
                    : 'border-twin-chat-divider text-twin-chat-fg-muted hover:text-twin-chat-fg',
                )}
                data-testid={`qs-quote-version-filter-option`}
              >
                {v} · {count}
              </button>
            )
          })}
        </div>
      )}
      {featured && <FeaturedQuote item={featured} comparisonLabels={comparisonLabels} />}
      {rest.length > 0 && (
        <QuotesGrid
          items={rest}
          total={shown.length}
          comparisonLabels={comparisonLabels}
          documentMode={documentMode}
        />
      )}
    </div>
  )
}

export type OpenEndTab = 'themes' | 'quotes'

const TAB_LABELS: Record<OpenEndTab, string> = {
  themes: 'Themes',
  quotes: 'Quotes',
}

/**
 * The available OE views for a given render context — the SINGLE source of truth for the
 * Themes|Quotes toggle. The card uses it internally (uncontrolled), and MessageRow calls it
 * to build the lifted PlotCard header toggle (controlled chat mode) so both agree exactly.
 * Mirrors the internal `tabs` derivation: documentMode → no views; report (jump-to-source)
 * → themes only; otherwise themes, plus quotes when there are verbatims. `themes` is accepted
 * for a stable call shape but does not affect the view set (matching today's derivation).
 */
export function availableOpenEndTabs({
  responses,
  documentMode = false,
  reportMode = false,
}: {
  themes?: Array<OpenEndTheme>
  responses?: Array<OpenEndResponse>
  documentMode?: boolean
  reportMode?: boolean
}): Array<OpenEndTab> {
  if (documentMode) return []
  if (reportMode) return ['themes']
  return (responses ?? []).length > 0 ? ['themes', 'quotes'] : ['themes']
}

export function QuickStudyOpenEndCard({
  themes,
  responses,
  question,
  personas,
  populationId,
  onJumpToSource,
  comparisonLabels = [],
  documentMode = false,
  activeTab,
  onTabChange,
  filteredEmpty = false,
  themeSplits,
  whyKey,
  scoreBadge,
  versionLabelById,
  themesView = 'heatmap',
  themePlot,
  quotePlot,
  themeSplitsPlot,
  className,
}: QuickStudyOpenEndCardProps) {
  // Legacy report-panel context: themes only + jump link. Document mode (v2 report) shows
  // themes and quotes inline with nothing hidden behind tabs or clamps.
  const reportMode = !documentMode && Boolean(onJumpToSource)
  const tabs = availableOpenEndTabs({ themes, responses, documentMode, reportMode })

  // Controlled (chat / PlotCard) mode: the active view is dictated by the `activeTab` prop and
  // the internal toggle is suppressed. Uncontrolled (legacy report / preview) mode keeps its
  // own tab state and renders byte-identically to before.
  const isControlled = activeTab !== undefined
  const [tab, setTab] = useState<OpenEndTab>(() => (themes?.length ? 'themes' : 'quotes'))
  const uncontrolledTab = tabs.includes(tab) ? tab : 'themes'
  const effectiveTab: OpenEndTab = isControlled ? activeTab : uncontrolledTab
  const hasThemes = (themes ?? []).some((t) => (t.theme || '').trim().length > 0)
  // Stamped reads (wave 7) draw through the shared plots; a combo band split keeps the legacy
  // view, whose split control and band heatmap the pooled plot does not carry.
  const stampedThemes = themeSplits ? null : readThemePlot(themePlot)
  const stampedQuotes = readQuotePlot(quotePlot)
  const stampedSplitHeat = themeSplits ? readThemeHeatPlot(themeSplitsPlot) : null

  if ((documentMode || reportMode) && !hasThemes) {
    return null
  }

  function renderThemes(mode: { documentMode?: boolean; reportMode?: boolean; withSplits?: boolean }) {
    if (stampedThemes) {
      return <OpenEndThemesPlot plot={stampedThemes} documentMode={mode.documentMode} />
    }
    return (
      <ThemesView
        themes={themes ?? []}
        documentMode={mode.documentMode}
        reportMode={mode.reportMode}
        comparisonLabels={comparisonLabels}
        themeSplits={mode.withSplits ? themeSplits : undefined}
        themesView={mode.withSplits ? themesView : undefined}
        heatPlot={mode.withSplits ? stampedSplitHeat : null}
      />
    )
  }

  function renderQuotes() {
    if (stampedQuotes) {
      return (
        <OpenEndQuotesPlot
          plot={stampedQuotes}
          responses={responses ?? []}
          personas={personas}
          populationId={populationId}
          comparisonLabels={comparisonLabels}
          filteredEmpty={filteredEmpty}
        />
      )
    }
    return (
      <QuotesView
        responses={responses ?? []}
        question={question}
        personas={personas}
        populationId={populationId}
        comparisonLabels={comparisonLabels}
        filteredEmpty={filteredEmpty}
        whyKey={whyKey}
        scoreBadge={scoreBadge}
        versionLabelById={versionLabelById}
      />
    )
  }

  return (
    <div className={cn('w-full space-y-3', className)} data-testid="qs-open-end-card">
      {documentMode ? (
        renderThemes({ documentMode: true })
      ) : isControlled ? (
        // Controlled chat mode: render ONLY the active view; the toggle lives upstream. The combo
        // Why-tab maps to the themes view (+ split control); the combo Quotes tab reads the whys +
        // stamps score badges. Both combo props are inert on a plain-OE card.
        <>
          {effectiveTab === 'themes' && renderThemes({ reportMode, withSplits: true })}
          {effectiveTab === 'quotes' && renderQuotes()}
        </>
      ) : (
        <>
      {/* Themes / Quotes toggle — hidden when there is only one view. */}
      {tabs.length > 1 && (
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-0.5 rounded-full border border-twin-chat-divider bg-twin-chat-bg p-0.5">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  uncontrolledTab === t
                    ? 'bg-twin-chat-surface text-twin-chat-fg'
                    : 'text-twin-chat-fg-muted hover:text-twin-chat-fg',
                )}
                data-testid={`qs-open-end-tab-${t}`}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      )}

      {uncontrolledTab === 'themes' && renderThemes({ reportMode })}
      {uncontrolledTab === 'quotes' && renderQuotes()}

      {/* Report context: Quotes live on the source insight in the main thread. */}
      {reportMode && (
        <button
          type="button"
          onClick={onJumpToSource}
          className="flex items-center gap-1 text-xs font-medium text-twin-chat-fg transition-colors hover:underline"
          data-testid="qs-open-end-jump-to-source"
        >
          <Quote className="h-3 w-3" />
          View quotes in chat
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
        </>
      )}
    </div>
  )
}

export default QuickStudyOpenEndCard
